import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Dialog, DialogContent } from "@mui/material";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { EmployeeCreateForm } from "./employeeCreation";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import Tabs from "./tabs";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import EmployeeEditModal from "./employeeEdit";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { QR, qrDialogPaperSx, QrModalHeader } from "../../qrCode/components/qrUi";
import { NameCell, ChipList, MutedText, filterBoxSx } from "../../leadMangement/components/leadUi";
import { EmpTypeChip, empTypeColor } from "./employeeUi";

export default function EmployeeList() {
    const { getEmployees, status, error } = useSelector((state) => state.employees);
    const [openModal, setOpenModal] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [rowData, setRowData] = useState([]);

    const uniqueEmployees = useMemo(() => {
        const seen = new Map();
        getEmployees.forEach((item) => {
            if (!seen.has(item.employee_type)) {
                seen.set(item.employee_type, {
                    value: item.employee_type_id,
                    label: item.employee_type,
                });
            }
        });
        return Array.from(seen.values()).sort((a, b) => a.value - b.value);
    }, [getEmployees]);

    // ✅ Default all selected when options are ready
    const [searchEmp, setSearchEmp] = useState([]);
    useEffect(() => {
        if (uniqueEmployees.length > 0) {
            setSearchEmp(uniqueEmployees); // select all by default
        }
    }, [uniqueEmployees]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const columns = useMemo(
        () => [
            { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
            {
                accessorKey: "username",
                header: "User Name",
                Cell: ({ row }) => (
                    <NameCell
                        name={row.original.username}
                        title="Edit employee"
                        onClick={() => {
                            setRowData(row.original);
                            setOpenModal(true);
                        }}
                    />
                )
            },
            {
                accessorKey: "employee_name",
                header: "Name",
                Cell: ({ row }) => (
                    <Typography variant="body2" fontWeight={600} color="#334155" noWrap>
                        {row.original.employee_name || "-"}
                    </Typography>
                ),
            },
            {
                accessorKey: "employee_type",
                header: "Employee Type",
                Cell: ({ row }) => <EmpTypeChip label={row.original.employee_type} id={row.original.employee_type_id} />,
            },
            {
                accessorKey: "emp_mobile",
                header: "Mobile",
                Cell: ({ row }) => (
                    <Typography variant="body2" color="#334155" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {row.original.emp_mobile || "-"}
                    </Typography>
                ),
            },
            { accessorKey: "emp_email", header: "Email", Cell: ({ row }) => <MutedText value={row.original.emp_email} /> },
            { accessorKey: "department_name", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_name} color={QR.purple} /> },
            { accessorKey: "branch_name", header: "Branch", Cell: ({ row }) => <ChipList value={row.original.branch_name} color={QR.blue} /> },
        ], []
    );

    const handleMultiSelectChange = (_name, selectedValues) => {
        setSearchEmp(selectedValues);
    };

    const renderEmployee = () => {
        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
                <Box sx={filterBoxSx}>
                    <FilterMultiSelect
                        name="searchEmp"
                        label="Employee Type"
                        options={uniqueEmployees}
                        value={searchEmp}
                        onChange={handleMultiSelectChange}
                    />
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    // Summary cards (display only, from the loaded employees): total + one per employee type
    const all = getEmployees || [];
    const stats = [
        { label: "Total Employees", value: all.length, icon: <PeopleAltIcon />, color: QR.main },
        ...uniqueEmployees.slice(0, 4).map((t) => ({
            label: t.label || "Other",
            value: all.filter((e) => e.employee_type_id === t.value).length,
            icon: <BadgeOutlinedIcon />,
            color: empTypeColor(t.value),
        })),
    ];

    return (
        <div>
            <Tabs stats={stats} onCreate={() => setCreateOpen(true)}>
                <DataTableWithCheckbox
                    enableRowSelection={false}
                    columns={columns}
                    data={getEmployees.filter((item) =>
                        searchEmp.map((item) => item.value).includes(item.employee_type_id)
                    ) || []}
                    renderFilters={renderEmployee()}
                    status={status}
                    searchFirst
                    emptyMessage={all.length ? "No employees match the selected types." : "No employees yet. Create one to get started."}
                />
            </Tabs>

            <EmployeeEditModal
                show={openModal}
                handleClose={() => setOpenModal(false)}
                rowData={rowData}
            />

            {/* Create employee popup */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: { sm: 920 } } }}
            >
                <QrModalHeader
                    icon={<PersonAddAlt1Icon />}
                    title="Create Employee"
                    subtitle="Add login details, then choose the role and modules they can access"
                    onClose={() => setCreateOpen(false)}
                />
                <DialogContent sx={{ p: 0 }}>
                    {/* remounts on each open, so the form starts empty */}
                    {createOpen && (
                        <EmployeeCreateForm
                            embedded
                            onCancel={() => setCreateOpen(false)}
                            onSuccess={(message) => {
                                setCreateOpen(false);
                                setSnackbar({ open: true, message: message || "Employee created", severity: "success" });
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}
